import { checkInputTags } from './check-input-tags.util';
import { checkSelection } from './check-selection.util';
import { isShortcutPressed } from './is-shortcut-pressed.util';
import { Shortcut } from './shortcut.model';

export type ScopeMode = 'override' | 'merge';

interface Binding {
  readonly config?: Config;
  readonly handler: (event: KeyboardEvent) => boolean | void;
  readonly shortcut: Shortcut;
}

interface Config {
  readonly allowWithInputs?: boolean;
}

export interface Scope {
  readonly bindings: Binding[];
  readonly mode: ScopeMode;
  readonly name: string;
}

let attachedEvent = false;
let scopes: Scope[] = [];

export class ShortcutsManager {
  #bindings: Binding[] = [];
  #created = false;
  #mode!: ScopeMode;
  #scope?: string;

  add(shortcut: Shortcut | Shortcut[], handler: (event: KeyboardEvent) => void, config?: Config): this {
    if (this.#created) {
      throw new Error('Cannot add shortcut. Object has already created');
    }

    if (Array.isArray(shortcut)) {
      shortcut.forEach((s) => this.#bindings.push({ config, handler, shortcut: s }));
    } else {
      this.#bindings.push({ config, handler, shortcut });
    }

    return this;
  }

  destroy(): void {
    if (!this.#created) {
      return;
    }

    const shortcuts = this.#bindings.map((binding) => binding.shortcut);
    const scope = scopes.find((s) => s.name === (this.#scope ?? 'global'));

    if (scope) {
      const bindings = scope.bindings.filter((binding) => !shortcuts.includes(binding.shortcut));

      scopes = bindings.length
        ? scopes.map((s) => s === scope ? { ...s, bindings } : s)
        : scopes.filter((s) => s !== scope);

      this.#scope = '';
      this.#bindings = [];
    }

    ShortcutsManager.detachEvents();
  }

  merge(): this {
    this.#mode = 'merge';
    this.#create();

    return this;
  }

  override(): this {
    this.#mode = 'override';
    this.#create();

    return this;
  }

  scope(scope: string): this {
    if (this.#created) {
      throw new Error('Cannot set scope. Object has already created');
    }

    if (this.#scope) {
      throw new Error(`Cannot set scope. Scope "${this.#scope}" already exists`);
    }

    this.#scope = scope;

    return this;
  }

  #create(): void {
    const scopeName = this.#scope ?? 'global';

    if (this.#created) {
      throw new Error('Cannot create object. Mode already exists');
    }

    if (this.#mode === 'override' && scopeName === 'global') {
      throw new Error('Cannot use "override" method if scope is "global"');
    }

    const scope = scopes.find((s) => s.name === scopeName);

    if (scope) {
      const existingShortcuts = this.#bindings
        .map(({ shortcut }) => shortcut)
        .filter((shortcut) => scope.bindings.some((b) => b.shortcut === shortcut));

      if (existingShortcuts.length) {
        throw new Error(`Cannot add shortcuts to the scope "${scope.name}". Shortcuts "${existingShortcuts.join(', ')}" already exist`);
      }

      if (scope.mode !== this.#mode) {
        throw new Error(`Cannot set different modes in one scope. Current mode: "${scope.mode}", new mode: "${this.#mode}"`);
      }

      scopes = scopes.map((s) => s === scope ? { ...s, bindings: [...s.bindings, ...this.#bindings] } : s);
    } else {
      scopes = [...scopes, {
        bindings: this.#bindings,
        mode: this.#mode,
        name: scopeName,
      }];
    }

    this.#created = true;

    ShortcutsManager.attachEvents();
  }

  static attachEvents(): void {
    if (!attachedEvent) {
      attachedEvent = true;
      document.addEventListener('keydown', this.handleKeydownEvent);
      window.addEventListener('standaloneBack', this.handleStandaloneBackEvent);
    }
  }

  static detachEvents(): void {
    if (attachedEvent && !scopes.length) {
      attachedEvent = false;
      document.removeEventListener('keydown', this.handleKeydownEvent);
      window.removeEventListener('standaloneBack', this.handleStandaloneBackEvent);
    }
  }

  static handleKeydownEvent(event: KeyboardEvent): void {
    if (event.repeat || checkSelection(event)) {
      return;
    }

    const isInput = checkInputTags(event);

    for (let i = scopes.length - 1; i >= 0; i--) {
      const { bindings, mode } = scopes[i];

      for (const { config, handler, shortcut } of bindings) {
        if (!config?.allowWithInputs && isInput) {
          continue;
        }

        if (isShortcutPressed(shortcut, event)) {
          if (!isInput) {
            // Closes all context menus to prevent unwanted actions via basic hotkeys [FVDR-758]
            document.body.click();
          }

          if (handler(event) ?? true) {
            event.preventDefault();

            return;
          }
        }
      }

      if (mode === 'override') {
        return;
      }
    }
  }

  static handleStandaloneBackEvent(): void {
    ShortcutsManager.handleKeydownEvent(new KeyboardEvent('keydown', { code: 'Back' }));
  }
}
